import { useState } from 'react';
import { useQuery } from './useQuery';
import { DocumentItem } from '../types/cabinet.types';
import {
  getDocuments,
  uploadDocument as apiUploadDocument,
  deleteDocument as apiDeleteDocument,
} from '../api/cabinetApi';
import { useAuth } from './useAuth';

export const useDocuments = () => {
  const { isAuthenticated } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null); // id документа що видаляється
  const [uploadBlocked, setUploadBlocked] = useState(false); // true якщо немає patientId в Medyc
  const [localError, setLocalError] = useState<Error | null>(null);

  const { data, isLoading, error: queryError, mutate, refetch } = useQuery(
    'documents',
    getDocuments,
    isAuthenticated
  );

  const documents = data || [];
  const error = localError || queryError;

  const uploadFile = async (file: File): Promise<boolean> => {
    setIsUploading(true);
    setLocalError(null);
    setUploadBlocked(false);
    try {
      const newDoc = await apiUploadDocument(file);
      mutate((prev: DocumentItem[] | null) => [newDoc, ...(prev || [])]);
      return true;
    } catch (err: any) {
      // 403 no_medyc_patient — upload заблокований до першої консультації
      if (err?.response?.status === 403 || err?.response?.data?.code === 'no_medyc_patient') {
        setUploadBlocked(true);
        throw new Error('upload_blocked');
      }
      setLocalError(err instanceof Error ? err : new Error('Failed to upload document'));
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Видалення власного документа.
   * Optimistic update: одразу прибираємо з UI, відновлюємо при помилці.
   */
  const deleteFile = async (doc: DocumentItem): Promise<boolean> => {
    if (!doc.canDelete) return false;

    setIsDeleting(doc.id);
    
    // Optimistic remove
    mutate((prev: DocumentItem[] | null) => (prev || []).filter((d) => d.id !== doc.id));

    try {
      await apiDeleteDocument(doc.id);
      return true;
    } catch (err) {
      // Відновлюємо документ при помилці
      mutate((prev: DocumentItem[] | null) => 
        [doc, ...(prev || [])].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      );
      setLocalError(err instanceof Error ? err : new Error('Failed to delete document'));
      return false;
    } finally {
      setIsDeleting(null);
    }
  };

  return {
    documents,
    isLoading,
    isUploading,
    isDeleting,
    uploadBlocked,
    error,
    uploadFile,
    deleteFile,
    refetch,
  };
};
